/**
 * Escalation Rules - Phase 15
 * 
 * Response guidance and escalation recommendations.
 */

import { ALERT_TYPES, ALERT_SEVERITY } from './alertModel';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

// ==================== ESCALATION GUIDANCE ====================

const ESCALATION_GUIDANCE = {
  [ALERT_TYPES.CRITICAL_OPEN_INCIDENTS]: {
    description: 'Critical incident requires immediate attention',
    urgency: 'high',
    suggestedActions: [
      'Open Incident Audit Trail to review critical incident details',
      'Identify affected terminal/location',
      'Contact location manager or cashier if on-site',
      'Generate diagnostics bundle for support'
    ],
    escalationRequired: true,
    escalationTarget: 'Support or IT Admin'
  },
  
  [ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD]: {
    description: 'Multiple blocked payments detected - potential revenue impact',
    urgency: 'high',
    suggestedActions: [
      'Review blocked payment panel in POS Reconciliation',
      'Contact cashier/location to verify payment status',
      'Attempt payment recovery retry if appropriate',
      'Check terminal connectivity and printer status'
    ],
    escalationRequired: true,
    escalationTarget: 'Manager or Finance Team'
  },
  
  [ALERT_TYPES.STALE_PENDING_QUEUE]: {
    description: 'Sync queue has stale items that need attention',
    urgency: 'medium',
    suggestedActions: [
      'Generate diagnostics bundle for affected scope',
      'Check terminal/device connectivity',
      'Review network diagnostics',
      'Consider manual retry of stuck items'
    ],
    escalationRequired: false,
    escalationTarget: 'IT Admin'
  },
  
  [ALERT_TYPES.REPEATED_SYNC_FAILURES]: {
    description: 'Multiple sync failures indicate a recurring problem',
    urgency: 'medium',
    suggestedActions: [
      'Check backend reachability',
      'Review diagnostics bundle for error patterns',
      'Verify API credentials are valid',
      'Check for rate limiting or service outages'
    ],
    escalationRequired: false,
    escalationTarget: 'IT Admin or Support'
  },
  
  [ALERT_TYPES.TERMINAL_UNSYNCED_ORDERS]: {
    description: 'Terminal has backlog of unsynced orders',
    urgency: 'medium',
    suggestedActions: [
      'Check terminal network connection',
      'Open POS Reconciliation for terminal',
      'Verify orders are properly formatted',
      'Consider manual order submission if critical'
    ],
    escalationRequired: false,
    escalationTarget: 'Location Manager'
  },
  
  [ALERT_TYPES.LOCATION_REPEATED_ISSUES]: {
    description: 'Location has recurring reliability issues',
    urgency: 'medium',
    suggestedActions: [
      'Review location reliability report',
      'Identify pattern (time of day, specific terminal, etc.)',
      'Schedule terminal/network inspection',
      'Consider hardware replacement if issues persist'
    ],
    escalationRequired: false,
    escalationTarget: 'Operations Manager'
  },
  
  [ALERT_TYPES.NO_SUCCESSFUL_SYNC_RECENTLY]: {
    description: 'No successful sync in extended period',
    urgency: 'high',
    suggestedActions: [
      'Verify device is online and connected',
      'Check network diagnostics',
      'Review recent sync log for errors',
      'Attempt manual sync trigger'
    ],
    escalationRequired: true,
    escalationTarget: 'IT Admin'
  },
  
  [ALERT_TYPES.CONFLICT_RATE_HIGH]: {
    description: 'High rate of sync conflicts detected',
    urgency: 'low',
    suggestedActions: [
      'Review conflict resolution patterns',
      'Check for multi-device editing of same records',
      'Verify deduplication logic is working',
      'Consider workflow changes to reduce conflicts'
    ],
    escalationRequired: false,
    escalationTarget: 'IT Admin'
  },
  
  [ALERT_TYPES.RELIABILITY_STATUS_AT_RISK]: {
    description: 'Overall reliability status has degraded',
    urgency: 'medium',
    suggestedActions: [
      'Review reliability reporting dashboard',
      'Identify top contributing factors',
      'Address critical and blocked payment incidents first',
      'Generate comprehensive diagnostics bundle'
    ],
    escalationRequired: true,
    escalationTarget: 'Operations or IT Admin'
  },
  
  [ALERT_TYPES.FAILED_KITCHEN_RECEIPT_BACKLOG]: {
    description: 'Kitchen/receipt failures affecting service',
    urgency: 'medium',
    suggestedActions: [
      'Check kitchen display system connectivity',
      'Verify receipt printer is online',
      'Retry failed items',
      'Consider manual kitchen ticket printing'
    ],
    escalationRequired: false,
    escalationTarget: 'Location Manager'
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get escalation guidance for an alert
 * @param {Object} alert
 * @returns {Object}
 */
export function getEscalationGuidance(alert) {
  const guidance = ESCALATION_GUIDANCE[alert.alertType];
  
  if (!guidance) {
    return {
      description: 'Review alert details and take appropriate action',
      urgency: 'medium',
      suggestedActions: [
        'Review alert details',
        'Check related incidents',
        'Generate diagnostics if needed'
      ],
      escalationRequired: false,
      escalationTarget: 'Manager'
    };
  }
  
  // Adjust urgency based on severity
  let urgency = guidance.urgency;
  if (alert.severity === ALERT_SEVERITY.CRITICAL) {
    urgency = 'high';
  }
  
  return {
    ...guidance,
    urgency
  };
}

/**
 * Get suggested next actions for an alert
 * @param {Object} alert
 * @returns {Array<Object>}
 */
export function getSuggestedNextActions(alert) {
  const guidance = getEscalationGuidance(alert);
  const actions = [];
  
  // Add view-related actions based on alert type
  if (alert.relatedIds?.incidentId) {
    actions.push({
      label: 'View Related Incident',
      action: 'view_incident',
      url: `${bo('incident-audit')}?incidentId=${alert.relatedIds.incidentId}`
    });
  }
  
  // Add scope-specific diagnostics
  if (alert.scope?.terminalId) {
    actions.push({
      label: 'Generate Terminal Diagnostics',
      action: 'view_diagnostics',
      url: `${bo('support-diagnostics')}?terminalId=${alert.scope.terminalId}`
    });
  } else if (alert.scope?.locationId) {
    actions.push({
      label: 'Generate Location Diagnostics',
      action: 'view_diagnostics',
      url: `${bo('support-diagnostics')}?locationId=${alert.scope.locationId}`
    });
  }
  
  // Add type-specific actions
  if (alert.alertType === ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD) {
    actions.push({
      label: 'Open POS Reconciliation',
      action: 'view_reconciliation',
      url: `${bo('pos-reconciliation')}?tab=blocked-payments`
    });
  }
  
  if (alert.alertType === ALERT_TYPES.RELIABILITY_STATUS_AT_RISK) {
    actions.push({
      label: 'View Reliability Report',
      action: 'view_reliability',
      url: bo('reliability-reporting')
    });
  }
  
  // Add text-based suggestions
  for (const suggestion of guidance.suggestedActions.slice(0, 3)) {
    actions.push({
      label: suggestion,
      action: 'suggestion',
      url: null
    });
  }
  
  return actions;
}

/**
 * Determine if escalation should be required
 * @param {Object} alert
 * @returns {boolean}
 */
export function shouldRequireEscalation(alert) {
  // Critical always requires escalation
  if (alert.severity === ALERT_SEVERITY.CRITICAL) {
    return true;
  }
  
  const guidance = ESCALATION_GUIDANCE[alert.alertType];
  return guidance?.escalationRequired || false;
}

/**
 * Get cross-link URLs for an alert
 * @param {Object} alert
 * @returns {Object}
 */
export function getAlertCrossLinks(alert) {
  const links = {};
  const scope = alert.scope || {};
  
  // Always include incident audit link
  links.incidentAudit = bo('incident-audit');
  
  // Scope-specific links
  if (scope.terminalId) {
    links.diagnostics = `${bo('support-diagnostics')}?terminalId=${scope.terminalId}`;
    links.reliability = `${bo('reliability-reporting')}?terminalId=${scope.terminalId}`;
  } else if (scope.locationId) {
    links.diagnostics = `${bo('support-diagnostics')}?locationId=${scope.locationId}`;
    links.reliability = `${bo('reliability-reporting')}?locationId=${scope.locationId}`;
  } else {
    links.diagnostics = bo('support-diagnostics');
    links.reliability = bo('reliability-reporting');
  }
  
  // Type-specific links
  if (['blocked_payments_threshold', 'terminal_unsynced_orders'].includes(alert.alertType)) {
    links.posReconciliation = bo('pos-reconciliation');
  }
  
  return links;
}

export default {
  getEscalationGuidance,
  getSuggestedNextActions,
  shouldRequireEscalation,
  getAlertCrossLinks
};
